'use client';

import { clsx } from 'clsx';
import { capitalize } from 'lodash';
import { FC, ReactNode } from 'react';

import { BadgeIllustration } from 'src/general/components/BadgeIllustration/BadgeIllustration';
import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Button } from 'src/general/components/Button/Button';
import { useModal } from 'src/hooks/useModal';

import { getManufacturerWarranty } from 'src/pdp/helpers/manufacturerWarranty.helper';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { WarrantyInfoModal } from '../WarrantyInfoModal/WarrantyInfoModal';
import styles from './WarrantyInfo.module.scss';

/**
 * TODO - Hardcoded as a temporary solution.
 * We will revisit how extended manufacturer warranty status is determined when the feature is more well-defined.
 */
const makesWithExtendedManufacturerWarranty = ['Toyota', 'Mitsubishi'];

interface WarrantyInfoProps {
  productDetails: ProductDetailsPayload;
  hasManufacturerWarranty: boolean; // applied for car with at least 3 months and 5000km/unlimited remaining warranty - manufacturerWarranty.helper
  extendedCoverageAvailable: boolean; // applied for cars with < 160000 odo, this is EC from Carma - refer to CarOverview.tsx.
  microcopy: {
    title: ReactNode;
    manufacturerExtendedCoverageContent: ReactNode;
    warrantyWithExtendedCoverageContent: ReactNode;
    onlyExtendedCoverageContent: ReactNode;
    modalButtonLinkLabel: ReactNode;
  };
}

export const WarrantyInfo: FC<WarrantyInfoProps> = ({
  productDetails,
  hasManufacturerWarranty,
  extendedCoverageAvailable,
  microcopy,
}) => {
  const { isVisible, open, close } = useModal();
  const formattedMake = capitalize(productDetails.make);
  const inInMakeWithExtendedManufacturerWarranty = makesWithExtendedManufacturerWarranty.includes(formattedMake);
  const extendedManufacturerWarrantyAvailable =
    extendedCoverageAvailable && hasManufacturerWarranty && inInMakeWithExtendedManufacturerWarranty; // this is EC from the make manufacturer

  return (
    <>
      <div className={clsx(styles.wrapper)}>
        {hasManufacturerWarranty && (
          <div className={styles.container}>
            <div className={styles.imgContainer}>
              <BadgeIllustration icon={BADGE.WARRANTY} background="gradient" shape="circle" className={styles.icon} />
            </div>
            <div className={styles.content}>
              <h6 className={clsx(styles.title, 'c-fs-caption', 'c-fw-bold')}>
                {microcopy.title}
                {inInMakeWithExtendedManufacturerWarranty && '*'}
              </h6>
              <p className={clsx(styles.description, 'c-fs-caption')}>{getManufacturerWarranty(productDetails)}</p>
            </div>
          </div>
        )}

        {extendedCoverageAvailable && (
          <div
            className={clsx(styles.eligibleContainer, {
              [styles.onlyEligibleContainer]: !hasManufacturerWarranty,
            })}
          >
            <div className={styles.imgContainer}>
              <BadgeIllustration
                icon={BADGE.EXTENDED_COVERAGE_WARRANTY}
                background="none"
                shape="square"
                className={styles.logo}
              />
            </div>
            <div
              className={clsx(styles.eligibleContent, {
                [styles.onlyEligibleContent]: !hasManufacturerWarranty,
              })}
            >
              <span className={clsx(styles.text, 'c-fs-caption')}>
                {hasManufacturerWarranty
                  ? microcopy.warrantyWithExtendedCoverageContent
                  : microcopy.onlyExtendedCoverageContent}
              </span>
              &ensp;
              <Button variant="link" size="small" onClick={open} className={styles.modalLink}>
                {microcopy.modalButtonLinkLabel}
              </Button>
            </div>
          </div>
        )}
      </div>
      {extendedManufacturerWarrantyAvailable && (
        <p className={styles.manufacturerWarranty}>{microcopy.manufacturerExtendedCoverageContent}</p>
      )}
      <WarrantyInfoModal isOpen={isVisible} close={close} />
    </>
  );
};
